/**
 * Wizard step 5 — per-item creation results + ONE subtle confetti burst
 * (reduced-motion guarded, fires once). Failures keep their server reasons.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useEffect, useRef, type FC } from 'react';
import { useReducedMotion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { CheckCircle2, XCircle } from 'lucide-react';

import { Button } from '@/components/ui';

import type { CreateAcceptedResult } from '../use-create-accepted';

const DoneStep: FC<{
  result: CreateAcceptedResult;
  draftApplied: boolean;
  onClose: () => void;
}> = ({ result, draftApplied, onClose }) => {
  const reduce = useReducedMotion();
  const fired = useRef(false);
  const allOk = result.rows.every((row) => row.ok);

  useEffect(() => {
    if (reduce || fired.current || !allOk) return;
    fired.current = true;
    confetti({
      disableForReducedMotion: true,
      particleCount: 48,
      spread: 64,
      startVelocity: 32,
      gravity: 0.95,
      ticks: 180,
      scalar: 0.85,
      origin: { x: 0.5, y: 0.32 },
    });
  }, [reduce, allOk]);

  return (
    <div className="space-y-3">
      <p className="text-sm font-semibold">
        {allOk ? 'Library updated' : 'Partly saved'}
      </p>
      <ul className="space-y-1.5">
        {result.rows.map((row) => (
          <li key={row.label} className="flex items-start gap-2 text-xs">
            {row.ok ? (
              <CheckCircle2 className="mt-px h-3.5 w-3.5 shrink-0 text-success" aria-hidden />
            ) : (
              <XCircle className="mt-px h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden />
            )}
            <span>
              <span className="font-medium">{row.label}</span>
              {row.detail ? (
                <span className="block text-[11px] text-muted-foreground">{row.detail}</span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {draftApplied ? (
        <p className="rounded-md border border-primary/30 bg-primary/5 px-2 py-1.5 text-[11px] text-muted-foreground">
          Loaded into the draft — review the live preview, then Save Config.
        </p>
      ) : null}
      <div className="flex justify-end">
        <Button size="sm" onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  );
};

export default DoneStep;
